import { useEffect, useState, useCallback, useRef } from "react";
import { Button, Col, Input, Select, Row } from "antd";
import { useNavigate } from "react-router-dom";
import useGet from "../../../global/hooks/useGet";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { HiArrowPath, HiOutlineViewColumns } from "react-icons/hi2";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import CommonModal from "../../../global/components/CommonModal";
import MultiSelect from "../../../global/components/inputWidgets/Transfer/MultiSelect";
import { FaColumns } from "react-icons/fa";
import {
  getDealerNo,
  getOemNo,
  getUserData,
  hasRole,
} from "../../../global/hooks/getUserRole";
import {
  modelOptions,
  nodeTypeOptions,
  productOptions,
  siteTypeOptions,
} from "../../../utils/Common";
import { useOptions } from "../../../providers/OptionsProvider";

export const DeviceFilter = ({
  filter,
  handleFilterChange,
  handleFilter,
  handleReset,
  setShowModal,
  selectedRowKeys,
  setFilter,
  data,
  setColumnsValues,
}) => {
  const { configOptions } = useOptions();
  const [showCommonModal, setShowCommonModal] = useState(false);
  const navigate = useNavigate();
  const [visibleColumns, setVisibleColumns] = useState([]);
  const initializedRef = useRef(false);
  const localColumnValues =
    JSON.parse(localStorage.getItem("column"))?.find(
      (item) => item?.user_id === getUserData()?.user_id,
    )?.column_data || {};

  // Get default columns based on role permissions
  const getDefaultColumns = useCallback(() => {
    // Default columns matching Columns.js structure
    const allDefaultColumns = [
      "DeviceNo",
      "DeviceName",
      "DeviceDesc",
      ...(hasRole(1, 2, 3) ? ["OemName"] : []),
      ...(hasRole(1, 2, 3, 4, 5, 6) ? ["DealerName"] : []),
      "CustomerName",
      "NodeType",
      "Mac",
      "Product",
      "SiteType",
      "Model",
      "Type",
    ];

    return allDefaultColumns;
  }, []);

  // Generate column options from data
  const getColumnOptions = () => {
    if (!data?.data || data.data.length === 0) {
      // If no data, return default columns as options
      return getDefaultColumns().map((key) => ({
        id: key,
        value: key,
        label: key.replace(/([A-Z])/g, " $1").trim(),
      }));
    }

    const sampleObject = data.data[0];
    return Object.keys(sampleObject).map((key) => ({
      id: key, // "1", "2", "3", etc.
      value: key, // "DeviceNo", "DeviceName", etc.
      label: key.replace(/([A-Z])/g, " $1").trim(),
    }));
  };

  // Initialize with just column names
  useEffect(() => {
    // If we already have saved columns, use them
    if (localColumnValues?.Device) {
      if (!initializedRef.current) {
        setVisibleColumns(localColumnValues.Device);
        setColumnsValues(localColumnValues.Device);
        initializedRef.current = true;
      }
      return;
    }

    // If already initialized and no saved columns, don't reinitialize
    if (initializedRef.current) {
      return;
    }

    // Handle both cases: when data exists and when it doesn't
    let allColumnIds;

    if (data?.data && data.data.length > 0) {
      // Use columns from actual data
      allColumnIds = Object.keys(data.data[0]).map((key, index) => key);
    } else {
      // No data available - use default columns
      allColumnIds = getDefaultColumns();
    }

    setVisibleColumns(allColumnIds);
    setColumnsValues(allColumnIds);
    initializedRef.current = true;
  }, [data, getDefaultColumns, localColumnValues]);

  const modifiedProducts = getModifiedResponse(productOptions, "products");
  const modifiedModels = getModifiedResponse(modelOptions, "models");
  const modifiedOems = getModifiedResponse(configOptions, "oems");
  // const modifiedProductions = getModifiedResponse(optionsData, "productions");
  const modifiedSiteTypes = getModifiedResponse(siteTypeOptions, "sitetype");
  const modifiedNodeTypes = getModifiedResponse(nodeTypeOptions, "nodetype");

  const dealerOptionsUrl = filter?.OemNo
    ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${filter?.OemNo}/dealers/`
    : !hasRole(1, 2, 3) && getOemNo()
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${getOemNo()}/dealers/`
      : null;

  const {
    data: dealerOptions,
    loading: dealerOptionsLoading,
    error: dealerOptionsError,
  } = useGet(dealerOptionsUrl);

  const custmerOptionsUrl = filter?.DealerNo
    ? `${process.env.REACT_APP_API_URL}/api/dealers/${filter?.DealerNo}/customers/`
    : !hasRole(1, 2, 3, 4, 5, 6) && getDealerNo()
      ? `${process.env.REACT_APP_API_URL}/api/dealers/${getDealerNo()}/customers/`
      : null;

  const {
    data: custmerOptions,
    loading: custmerOptionsLoading,
    error: custmerOptionsError,
  } = useGet(custmerOptionsUrl);

  const modifiedDealers = getModifiedResponse(dealerOptions, null);
  const modifiedCustomers = getModifiedResponse(custmerOptions, null);

  return (
    <div className="">
      <CommonModal
        isVisible={showCommonModal}
        onClose={() => setShowCommonModal(false)}
        width={800}
        title="Column"
        icon={FaColumns}
        onConfirm={() => {
          setColumnsValues(visibleColumns);

          // Get existing data from localStorage
          const existingData = localStorage.getItem("column");
          let columnData = existingData ? JSON.parse(existingData) : [];

          const currentUserId = getUserData()?.user_id;

          // Find if current user already exists in the array
          const userIndex = columnData?.findIndex(
            (item) => item.user_id === currentUserId,
          );

          if (userIndex !== -1) {
            // User exists - merge with existing column_data to preserve other pages
            columnData[userIndex].column_data = {
              ...columnData[userIndex].column_data, // Keep existing data (Device, Log, etc.)
              Device: visibleColumns, // Update only Device
            };
          } else {
            // User doesn't exist - create new entry
            columnData.push({
              user_id: currentUserId,
              column_data: { Device: visibleColumns },
            });
          }

          // Save updated data back to localStorage
          localStorage.setItem("column", JSON.stringify(columnData));

          setShowCommonModal(false);
        }}
        content={
          <MultiSelect
            name="Column"
            label={`Search`}
            value={visibleColumns}
            options={getColumnOptions()}
            onChange={(name, selected) => {
              console.log(name, selected);
              setVisibleColumns(selected);
            }}
          />
        }
      />
      <div className="mt-2">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Input
              className="input_text_3 mr10"
              name="DeviceNo"
              placeholder="No"
              value={filter.DeviceNo}
              onChange={(e) => {
                const { value } = e.target;
                // Allow only positive integers or empty string
                if (/^[1-9]\d*$/.test(value) || value === "") {
                  handleFilterChange(e);
                }
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={6} xl={6} className="!h-[32px]">
            <Input
              className="input_text_3 mr10 !h-full"
              name="DeviceName"
              placeholder="Device Name"
              value={filter.DeviceName}
              onChange={handleFilterChange}
            />
          </Col>

          {hasRole(1, 2, 3) && (
            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Select
                showSearch
                placeholder="OEM"
                optionFilterProp="label"
                value={filter.OemNo ? Number(filter.OemNo) : undefined}
                onChange={(value) => setFilter({ ...filter, OemNo: value })}
                options={modifiedOems}
                style={{ width: "100%" }}
                allowClear
              />
            </Col>
          )}
          {hasRole(1, 2, 3, 4, 5, 6) && (
            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Select
                showSearch
                placeholder="Dealer"
                optionFilterProp="label"
                value={filter.DealerNo ? Number(filter.DealerNo) : undefined}
                onChange={(value) => setFilter({ ...filter, DealerNo: value })}
                options={modifiedDealers}
                style={{ width: "100%" }}
                allowClear
                disabled={
                  hasRole(4, 5, 6)
                    ? false
                    : filter.OemNo === undefined ||
                      filter.OemNo === null ||
                      filter.OemNo === ""
                }
              />
            </Col>
          )}
          {!hasRole(9, 10, 11, 12) && (
            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Select
                showSearch
                placeholder="Customer"
                optionFilterProp="label"
                value={
                  filter.CustomerNo ? Number(filter?.CustomerNo) : undefined
                }
                onChange={(value) =>
                  setFilter({ ...filter, CustomerNo: value })
                }
                options={modifiedCustomers}
                style={{ width: "100%" }}
                allowClear
                disabled={
                  hasRole(7, 8, 9)
                    ? false
                    : filter.DealerNo === undefined ||
                      filter.DealerNo === null ||
                      filter.DealerNo === ""
                }
              />
            </Col>
          )}
          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="Node Type"
              optionFilterProp="label"
              value={filter.NodeType ? Number(filter?.NodeType) : undefined}
              onChange={(value) => setFilter({ ...filter, NodeType: value })}
              options={modifiedNodeTypes}
              style={{ width: "100%" }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="Site Type"
              optionFilterProp="label"
              value={
                filter.SiteType || filter.SiteType === 0
                  ? Number(filter.SiteType)
                  : undefined
              }
              onChange={(value) => setFilter({ ...filter, SiteType: value })}
              // onSearch={onSearch}
              options={modifiedSiteTypes}
              style={{ width: "100%" }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={6} xl={6} className="!h-[32px]">
            <Input
              className="input_text_3 mr10 !h-full"
              name="Mac"
              placeholder="MAC"
              value={filter.Mac}
              onChange={handleFilterChange}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="Product"
              optionFilterProp="Product"
              value={filter.Product && Number(filter.Product)}
              onChange={(value) => setFilter({ ...filter, Product: value })}
              options={modifiedProducts}
              style={{ width: "100%" }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="Model"
              optionFilterProp="Model"
              value={
                filter.Model?.toString() ? Number(filter.Model) : undefined
              }
              onChange={(value) => setFilter({ ...filter, Model: value })}
              options={modifiedModels}
              style={{ width: "100%" }}
              allowClear
            />
          </Col>
          {/* <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="Node Type"
              optionFilterProp="NodeType"
              value={filter.NodeType || undefined}
              onChange={(value) => setFilter({ ...filter, NodeType: value })}
              options={modifiedCustomers}
              style={{ width: "100%" }}
            />
          </Col> */}

          {/* <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="Online"
              optionFilterProp="label"
              value={filter.Online}
              onChange={(value) => setFilter({ ...filter, Online: value })}
              options={modifiedOnlineStatus}
              style={{ width: "100%" }}
              allowClear
            />
          </Col> */}
          {/* <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Input
              className="input_text_3 mr10"
              name="Type"
              placeholder="Type"
              value={filter.Type}
              onChange={handleFilterChange}
            />
          </Col> */}

          {/* <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="Expired"
              optionFilterProp="Expired"
              value={filter.Expired || undefined}
              onChange={(value) => setFilter({ ...filter, Expired: value })}
              options={[
                {
                  value: 1,
                  label: "Yes",
                },
                {
                  value: 0,
                  label: "No",
                },
              ]}
              style={{ width: "100%" }}
            />
          </Col> */}
        </Row>
      </div>
      <div className="w-full overflow-x-auto">
        <div className="flex justify-between mt-4 flex-nowrap min-w-[610px]">
          <div className="flex items-center">
            <Button
              type="primary"
              className="mr-2 w-[104px] h-[30px] font-medium"
              onClick={handleFilter}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              <span>Apply</span>
            </Button>
            <Button
              className="!mr-2 reset-button w-[104px] h-[30px] font-medium"
              onClick={handleReset}
            >
              <HiArrowPath className="!text-[14px] font-medium" />
              Reset
            </Button>
            <Button
              type="primary"
              className="!mr-2 w-[104px] h-[30px] font-medium"
              onClick={() => setShowCommonModal(true)}
            >
              <HiOutlineViewColumns className="!text-[14px] font-medium" />
              Column
            </Button>
            {hasRole(1, 2) && (
              <Button
                type="primary"
                className="mr-2 w-[104px] h-[30px] font-medium"
                onClick={() => navigate("/device/add")}
              >
                <PlusOutlined className="" />
                Add
              </Button>
            )}
            {hasRole(1, 2) && (
              <Button
                type="primary"
                className="mr-2 w-[104px] h-[30px] font-medium"
                disabled={selectedRowKeys?.length === 0 ? true : false}
                onClick={() => {
                  setShowModal(true);
                }}
              >
                <DeleteOutlined className="" />
                Delete
              </Button>
            )}
          </div>
          <div>
            {/* <Button
              type="primary"
              className="mr-2 w-[30px] h-[30px]"
              onClick={() => navigate("/devicelist/add")}
            >
              <PlusOutlined className="" />
            </Button> */}
            {/* <Button
              type="primary"
              disabled={selectedRowKeys?.length === 0 ? true : false}
              className="w-[30px] h-[30px]"
              onClick={() => {
                setShowModal(true);
              }}
            >
              <DeleteOutlined className="" />
            </Button> */}
            {/* <Button
              type="primary"
              className="mr-2 h-[30px] font-medium"
              onClick={() => setShowCommonModal(true)}
            >
              <FaColumns />
              Column
            </Button> */}
          </div>
        </div>
      </div>
    </div>
  );
};
